import { useEffect, useMemo, useRef, useState } from 'react';
import type { BrandArtMode, BrandArtStyle } from '@icetus/contracts';
import { BRAND_ART_MODES, BRAND_ART_STYLES } from '@icetus/contracts';
import { Button } from '@icetus/ui';
import { ApiError, uploadImageBlob } from '../api';
import { useArtJob } from '../lib/use-art-job';
import { Modal } from './overlay';
import {
  artInkOf,
  artPlateFor,
  buildIconSvg,
  buildLogoSvg,
  buildPalettes,
  contrastBackdrop,
  logoSize,
  measureImage,
  paletteLegibility,
  relativeLuminance,
  renderSvgToPng,
  svgToDataUrl,
} from '../lib/brand-logo';

/**
 * 品牌标生成面板。
 *
 * ## 只有 AI 一条路
 *
 * 一开始这里是两个页签：浏览器里排版的"排版生成"（秒出、免费、字一定对），
 * 和服务端出图的"AI 生成"。前者的存在理由是"模型写不对公司名，尤其中文"。
 *
 * 但客户看过两边的东西之后给了一个很干脆的结论：**排版那条不要了**。理由是
 * 它产出的东西客户自己也不想要 —— 一个内置几何符号加一行系统字体，和"能用的标"
 * 之间差着一整个设计师。留着它只是让人多点一次才发现自己白点了。
 *
 * 所以现在入口只有 AI。代价是"绝不会错字"这层保障没了，用 `只画图形` 那一档
 * 找回来：模型只画符号，名字由我们用确定字体排上去。它仍然走同一份额度，
 * 因为花的那张图是真钱。
 *
 * ## 用这个 = 填进去 + 关窗
 *
 * 客户在这层弹窗里只做一件事：看那张图对不对。所以"用这个"就是终点 ——
 * 上传、回填、关窗，一步到底。中间再插一层"先用这张 → 再确认用这个"，
 * 客户要为一个动作点两次，而且第二次点的还是同一个意思。
 */
const ICON_SIZE = 256;
/**
 * 排版导出倍数（只有"只画图形"那条走这个值）。
 *
 * 页头的 `max-block-size` 是「大」档 48×1.2 ≈ 58px，二倍屏就要 116px 的真像素；
 * 而紧贴后的画布高只有 70 上下，1 倍图被放大会糊。2 倍是「够用且文件不肥」的点：
 * 一张 800×140 的 PNG 大约 20–40KB。
 */
const LOGO_SCALE = 2;

interface Note {
  tone: 'ok' | 'warn' | 'error';
  text: string;
}

/**
 * 已经认下来的那张图 —— 预览与导出**共用这一份**。
 *
 * `full` 是模型连名字一起画好的整张标，只差一个尺寸（比例由模型定，
 * 我们不能假设它是方的）；`symbol` 只有一个符号，名字由 `buildLogoSvg` 排。
 *
 * `plate` 是"它压在客户页头上会糊"时的托底色。算出来就固定住，
 * 不要在预览和导出之间各算一次：两次算出来的板子只要差一档，
 * 客户就会拿到一张和预览不一样的图。
 */
interface Adopted {
  kind: 'full' | 'symbol';
  url: string;
  /** 只有 `full` 有意义 */
  width: number;
  height: number;
  plate: string | null;
}

export function BrandGenerator({
  open,
  onClose,
  tenantId,
  siteId,
  siteName,
  brandColor,
  headerBg,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  siteId: string;
  siteName: string;
  /** 站点已配的品牌色，用来派生配色。没配就给默认蓝 */
  brandColor: string | null;
  /** 当前主题的页头底色。标最终就压在这上面，所以配色必须按它选 */
  headerBg: string;
  /** 生成并上传完成。父层负责填进表单（仍需点保存才生效） */
  onApply: (result: { logoUrl: string; faviconUrl: string }) => void;
}) {
  const [name, setName] = useState(siteName);
  /**
   * 整标还是只出图形。
   *
   * 默认 `full`：客户点开"生成品牌标"想要的是"一张能用的标"，而不是"一个符号"。
   * 字写错了再让他切到 symbol，比一开始就给半成品强。
   */
  const [artMode, setArtMode] = useState<BrandArtMode>('full');
  const [style, setStyle] = useState<BrandArtStyle>('geometric');
  const [prompt, setPrompt] = useState('');
  const [adopted, setAdopted] = useState<Adopted | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const applyingRef = useRef(false);
  /**
   * 出图那条链（发起、轮询、额度）在 `useArtJob` 里，与分类封面共用一份。
   * 面板关着的时候 `enabled=false`：轮询会停，也不会去拉额度。
   */
  const {
    available,
    job,
    running: aiJobRunning,
    error: jobError,
    start: startJob,
    reset: resetJob,
    refreshAvailability,
  } = useArtJob(tenantId, open);

  const palettes = useMemo(() => buildPalettes(brandColor, headerBg), [brandColor, headerBg]);
  /**
   * 名字与图标底板的配色由**页头底色**定，不给客户选。
   *
   * 这一栏以前是手动的（八档配色），但它只在"图形 + 我们自己排的字"下才有意义；
   * 现在没有排版这一路了，唯一还用到它的地方是 `只画图形` 时那行名字和
   * 浏览器图标的底板 —— 这两处都只有一个正确答案：压在这个页头上看得见的那一档。
   * 摆一排按钮让客户自己试，等于把一个我们算得出来的问题丢回给他。
   */
  const palette = useMemo(() => {
    const preferred = palettes.find((item) => item.key === 'brand') ?? palettes[0]!;
    const legibility = paletteLegibility(preferred, headerBg);
    if (legibility.ok) return preferred;
    return palettes.find((item) => item.key === legibility.suggestion) ?? preferred;
  }, [palettes, headerBg]);

  // 每次打开都按当前站点重置：上一次给别的站生成的标不能带过来
  useEffect(() => {
    if (!open) return;
    setName(siteName);
    setArtMode('full');
    setAdopted(null);
    setNote(null);
    resetJob();
    void refreshAvailability();
  }, [open, siteName, resetJob, refreshAvailability]);

  /**
   * 出新图就自动认领 —— 不再有"先用这张"那一步。
   *
   * 认领要做两件异步的事：量比例（整标是位图，比例由模型定）和量整体色
   * （决定要不要垫底板）。这两件事没做完之前不能让它可点，否则导出会拿到
   * 一张没垫板子的图 —— 也就是"预览里看得见、贴上去看不见"。
   *
   * 生成新的一张时**不清掉旧的**：清掉会让预览在等待期间闪成空白，
   * 而上一张本来就是客户已经看过的东西。
   */
  useEffect(() => {
    if (!job || job.status !== 'succeeded' || !job.artPreview) return;
    const url = job.artPreview;
    let alive = true;
    void (async () => {
      // 量色失败不算失败：`artPlateFor` 拿 null 会按页头亮度兜底
      const ink = await artInkOf(url).catch(() => null);
      if (!alive) return;
      const plate = artPlateFor(ink, headerBg);
      // 这个面板只会建 full / symbol 两种任务；别的一律不认领（防止以后加模式时误挂）
      if (job.renderMode === 'symbol') {
        setAdopted({ kind: 'symbol', url, width: 0, height: 0, plate });
        return;
      }
      try {
        const size = await measureImage(url);
        if (!alive) return;
        setAdopted({ kind: 'full', url, width: size.width, height: size.height, plate });
      } catch {
        if (alive) setNote({ tone: 'error', text: '这张图读不出尺寸，再生成一张试试' });
      }
    })();
    return () => {
      alive = false;
    };
  }, [job, headerBg]);

  /**
   * 预览与导出共用的那一份描述。
   *
   * 整标出现时里面的版式/符号/配色全部无关（`buildLogoSvg` 会忽略它们）；
   * 只画图形时用 `mark-left` —— 页头是横排的，符号在左最稳妥。
   */
  const options = useMemo(
    () => ({
      name: name.trim() || siteName,
      layout: 'mark-left' as const,
      mark: 'ring' as const,
      palette,
      // 保留模型给的原本配色：客户花一张图的钱，不该拿到一个单色几何块
      artUrl: adopted?.kind === 'symbol' ? adopted.url : null,
      artPlate: adopted?.kind === 'symbol' ? adopted.plate : null,
      lockup:
        adopted?.kind === 'full'
          ? { url: adopted.url, width: adopted.width, height: adopted.height }
          : null,
      lockupPlate: adopted?.kind === 'full' ? adopted.plate : null,
    }),
    [name, siteName, palette, adopted],
  );

  /**
   * 没有认领到图时**什么都不画**。
   *
   * `buildLogoSvg` 在没有图时会退回内置符号 + 名字，那是"排版生成"的产物 ——
   * 摆在只做 AI 的面板里，客户会以为这就是他能拿到的东西。
   */
  const logoSvgUrl = useMemo(
    () => (adopted ? svgToDataUrl(buildLogoSvg(options)) : null),
    [adopted, options],
  );
  const iconSvgUrl = useMemo(
    () => (adopted ? svgToDataUrl(buildIconSvg(options)) : null),
    [adopted, options],
  );
  /** 第二块预览的底色取"页头的反面"：白标压在白底上等于没画 */
  const contrastBg = contrastBackdrop(palette.ink);

  /** 发起 AI 生成，然后每 2 秒问一次结果 */
  /** 发起一次出图。轮询在 hook 里，这里只说"画什么" */
  const generateArt = () => {
    setNote(null);
    void startJob({
      siteId,
      style,
      mode: artMode,
      // full 模式要模型写字，名字必须给；symbol 模式下这个名字根本不进提示词
      wordmark: artMode === 'full' ? name.trim() || siteName : null,
      prompt: prompt.trim() || null,
      // 让模型用客户自己的品牌色系，出来的图形贴到站上才不会像个外人
      color: brandColor,
      /**
       * 这张标将来压在深色页头上。
       *
       * 不传这个，模型会忠实地画一张深色标 —— 客户品牌色是深蓝时，
       * 那张图压在近黑的页头上等于没画。0.28 是"深到需要用反白版"的分界：
       * 比它亮的页头上深色标还能读，比它暗的就必须换成浅色内容。
       */
      dark: relativeLuminance(headerBg) < 0.28,
    });
  };

  /**
   * 用这个：上传两张图 → 回填表单 → 关窗。
   *
   * 整标也要走一次合成（`buildLogoSvg` + canvas），**不能直接传 `adopted.url`**：
   * 底板只存在于 SVG 里，直接传位图，客户在面板里看到一块白板托着标，
   * 拿到的却是没有板的那张，压在近黑页头上就是一团看不见的深蓝。
   * **预览和交付必须是同一条路径**，否则"所见即所得"就是假的。
   *
   * 缩放比例按原图宽度取：整标是模型出的位图，缩一遍在页头上看不出差别，
   * 但放大会立刻糊。所以让比例去缩、不许它放大；上限 6 是给 3:1 横条
   * （合成画布 64 高、源图 1536 宽 ≈ 7 倍）留的一点余量，再大只是多几 MB。
   */
  const apply = async () => {
    if (!adopted) {
      setNote({ tone: 'warn', text: '先生成一张，满意了再点「用这个」' });
      return;
    }
    /**
     * 连点两下会传两份、还会在关窗之后又回填一次。
     * `busy` 只能拦住"下一次渲染之后"的点击，所以这里用 ref 兜住同一帧里的第二下。
     */
    if (applyingRef.current) return;
    applyingRef.current = true;
    setBusy(true);
    setNote(null);
    try {
      const stamp = Date.now().toString(36);
      const iconBlobPromise = renderSvgToPng(buildIconSvg(options), ICON_SIZE, ICON_SIZE);
      const box = logoSize(options);
      const scale =
        adopted.kind === 'full' ? Math.min(6, Math.max(2, adopted.width / box.width)) : LOGO_SCALE;
      const logoW = Math.round(box.width * scale);
      const logoH = Math.round(box.height * scale);
      const logoBlob = await renderSvgToPng(buildLogoSvg(options), logoW, logoH);
      const iconBlob = await iconBlobPromise;
      // 两张并行：串行会让"用这个"多等一个来回，而它们互不依赖
      const [logo, icon] = await Promise.all([
        uploadImageBlob(tenantId, {
          siteId,
          blob: logoBlob,
          filename: `brand-logo-${stamp}.png`,
          width: logoW,
          height: logoH,
        }),
        uploadImageBlob(tenantId, {
          siteId,
          blob: iconBlob,
          filename: `brand-icon-${stamp}.png`,
          width: ICON_SIZE,
          height: ICON_SIZE,
        }),
      ]);
      onApply({ logoUrl: logo.url, faviconUrl: icon.url });
      // 成功即关：客户下一步一定是"保存修改"，这层弹窗留着只会挡住它
      onClose();
    } catch (err) {
      setNote({
        tone: 'error',
        text: `没能保存这张标：${err instanceof ApiError || err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      applyingRef.current = false;
      setBusy(false);
    }
  };

  /**
   * 只有真拿到额度才显示。
   *
   * 接口挂了时 `available` 里那份 quota 的 limit 是 0 —— 显示成"还剩 0 张"
   * 会让客户以为额度没了，那比不显示更糟。
   */
  const quota = available?.quota && available.quota.limit > 0 ? available.quota : null;
  /**
   * 页脚那句话的优先级：本地提示 > 出图链报的错 > 按现状给下一步。
   * 出图链的错（发起失败、查不到进度）必须能顶到最显眼的位置，否则客户点了
   * 「生成」什么都没发生，只会再点一次。
   */
  const shown = note ?? (jobError ? { tone: 'error' as const, text: jobError } : null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="生成品牌标"
      footer={
        <div className="artgen__foot">
          <span className="artgen__footnote">
            {shown ? (
              <span
                className={`artgen__note is-${shown.tone}`}
                role={shown.tone === 'error' ? 'alert' : 'status'}
              >
                {shown.text}
              </span>
            ) : adopted ? (
              '点「用这个」只会填进表单，还要再点「保存修改」才会改线上'
            ) : (
              '先生成一张，满意了再点「用这个」'
            )}
          </span>
          <div className="artgen__footactions">
            <Button onClick={() => void apply()} disabled={busy || aiJobRunning || !adopted}>
              {busy ? '保存中…' : '用这个'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="brandgen">
        <div className="brandgen__body">
          {/*
            第一块就是**客户自己的页头**，不是一块我挑的深色。
            之前深色那块写死了 #101828，等于在替客户想象一种页头，而真页头可能
            是深棕、墨绿、纯黑 —— 标压上去看不见这件事，只有放在真底色上才露得出来。
          */}
          <div className="brandgen__preview">
            {adopted && logoSvgUrl && iconSvgUrl ? (
              <>
                <div className="brandgen__sample">
                  <div className="brandgen__panel" style={{ background: headerBg }}>
                    <img src={logoSvgUrl} alt="贴在你当前页头上的效果" />
                  </div>
                  <span className="brandgen__panelcap">你的页头</span>
                </div>
                <div className="brandgen__sample">
                  <div className="brandgen__panel" style={{ background: contrastBg }}>
                    <img src={logoSvgUrl} alt="换一种底色上的效果" />
                  </div>
                  <span className="brandgen__panelcap">换一种底色</span>
                </div>

                <div className="brandgen__icons">
                  <div className="brandgen__iconbox">
                    <img src={iconSvgUrl} alt="浏览器图标预览" />
                    <span>标签页 16px</span>
                  </div>
                  <div className="brandgen__iconmini">
                    <img src={iconSvgUrl} alt="" />
                    <span className="brandgen__tabtitle">{name.trim() || siteName}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="artgen__empty">
                <strong>还没有生成。</strong>
                <span>
                  {aiJobRunning
                    ? '正在画，10–30 秒。出图后这里会显示它贴在你页头上的样子。'
                    : '点右边的「生成」，出图后这里会显示它贴在你页头上的样子。'}
                </span>
              </div>
            )}
          </div>

          {/* 右：出图参数 */}
          <div className="brandgen__controls">
            {available && !available.available ? (
              <p className="artgen__blocked" role="status">
                {available.reason}
              </p>
            ) : null}

            {/*
              两排 chips：上面是模式（图形 + 文字 / 只画图形），下面是风格。
              同一片里排的时候，两个模式 chip 紧挨着五个风格 chip，客户看不出
              分界在哪、以为是一道七选一的题（选中态却是两处同时亮）。
              拆开之后**不恢复分组标题**：上一版那两个标题各占一整行，读两遍才
              知道哪边管什么。风格这一排只用「风格：」一个行内前缀，模式那排
              位置本来就在最上，不需要再说一遍。
            */}
            <div className="artgen__group">
              <div className="artgen__chips">
                {BRAND_ART_MODES.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`artgen__chip${artMode === item.key ? ' is-active' : ''}`}
                    onClick={() => {
                      setArtMode(item.key);
                      setNote(null);
                    }}
                    title={item.hint}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="artgen__styleline">
                <span className="artgen__chiplabel">风格：</span>
                <div className="artgen__chips">
                  {BRAND_ART_STYLES.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={`artgen__chip${style === item.key ? ' is-active' : ''}`}
                      onClick={() => setStyle(item.key)}
                      title={item.hint}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <label className="artgen__field">
              <span>{artMode === 'full' ? '要它写出来的名字' : '排在图形旁边的名字'}</span>
              <input
                className="field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={siteName}
                maxLength={48}
              />
            </label>

            <label className="artgen__field">
              <span>想让它像什么（可选）</span>
              <input
                className="field"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="写具体的物件：游标卡尺、齿轮、精密测量"
                maxLength={200}
              />
            </label>

            <div className="artgen__group">
              <Button
                onClick={generateArt}
                disabled={aiJobRunning || available?.available === false}
              >
                {aiJobRunning ? '正在生成…（10–30 秒）' : job ? '再生成一张' : '生成'}
              </Button>
              <p className="artgen__fineprint">
                {quota
                  ? `本月还剩 ${quota.remaining} 张（共 ${quota.limit} 张）。每点一次都会计入，包括生成失败的那次。`
                  : '每张图都是真金白银，所以每次生成都会计入本月额度。'}
              </p>
            </div>

            {job ? (
              <div className="brandgen__result">
                {job.status === 'succeeded' && job.artPreview ? (
                  <>
                    <img
                      src={job.artPreview}
                      alt={job.renderMode === 'full' ? 'AI 生成的整标' : 'AI 生成的图形'}
                      className={
                        job.renderMode === 'full'
                          ? 'brandgen__resultart is-lockup'
                          : 'brandgen__resultart'
                      }
                    />
                    <div className="brandgen__resultbody">
                      <p className="brandgen__ok">
                        {job.renderMode === 'full' ? '整标已生成' : '图形已生成'}
                      </p>
                      <p className="artgen__fineprint">
                        {job.renderMode === 'full'
                          ? '名字是模型写进图里的。逐字核一遍，尤其是中文 —— 对就点右下角「用这个」。'
                          : '它和名字已经排在左边预览里了，没问题就点右下角「用这个」。'}
                      </p>
                      <p className="artgen__fineprint">
                        这张图已经存进媒体库，之后在「媒体库」里也能找到、能删。
                      </p>
                    </div>
                  </>
                ) : job.status === 'failed' ? (
                  <p className="artgen__note is-error" role="alert">
                    {job.error ?? '生成失败，再试一次'}
                  </p>
                ) : (
                  <p className="brandgen__waiting">
                    {job.status === 'pending' ? '排队中…' : '正在画…'}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
